import { LitElement, html } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import '@shoelace-style/shoelace/dist/components/dialog/dialog.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import { styles as sharedStyles } from '../styles/shared-styles';
import { write } from '../language/lang';
import { Awaitable, awaitableTemplate } from '../utility/awaitable-data';
import type { SlDialog } from '@shoelace-style/shoelace';

export interface DialogPromises {
  /**
   * resolves after first becoming visible and the modal exists
   *  */
  onShow: Promise<void>;
  /**
   * resolves when the modal has been closed with hide modal.
   */
  onClose: Promise<void>;
}

@customElement('ep-custom-dialog')
export class DialogBase extends LitElement {
  // Pull in shared styles so the `full-page-on-phone` treatment applies to every
  // dialog built on this base (inherited by subclasses unless they override).
  static styles = [sharedStyles];

  @query('sl-dialog') dialog!: SlDialog;

  private _promises?: DialogPromises;
  protected label(): Awaitable {
    return html`<h2>${write`${'ref:dialog:default-title'}Information`}</h2>`;
  }
  protected headerActions(): Awaitable {
    return null;
  }
  content(): Awaitable {
    return '';
  }
  private releasePromises() {
    this._promises = undefined;
    document.body.removeChild(this);
  }
  connectedCallback(): void {
    super.connectedCallback();
    this.addEventListener('sl-request-close', this.internalCanClose);
    this.addEventListener('sl-after-hide', this.releasePromises);
  }
  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.removeEventListener('sl-request-close', this.internalCanClose);
  }
  private internalCanClose(
    event: CustomEvent<{ source: 'close-button' | 'keyboard' | 'overlay' }>
  ) {
    let canClose = this.canClose(event.detail.source);
    if (!canClose) {
      event.preventDefault();
    }
  }
  protected canClose(source: string): boolean {
    const event = new CustomEvent('ep-can-close', {
      detail: { source, canClose: true },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(event);
    return event.detail.canClose;
  }
  async run(): Promise<DialogPromises> {
    if (this._promises) return this._promises;
    document.body.appendChild(this);
    await this.updateComplete;
    const dialog = this.dialog;
    if (!dialog) throw new Error('Dialog not found');
    void dialog.show();
    this._promises = {
      onShow: new Promise((resolve) => {
        dialog.addEventListener('sl-after-show', () => {
          resolve();
        });
      }),
      onClose: new Promise((resolve) => {
        dialog.addEventListener('sl-after-hide', () => {
          resolve();
        });
      }),
    };
    return this._promises;
  }
  close() {
    void this.dialog.hide();
  }
  render() {
    const headerActions = awaitableTemplate(this.headerActions());
    const footer = awaitableTemplate(this.footer());
    const content = awaitableTemplate(this.content());
    const label = awaitableTemplate(this.label());
    ``;
    return html`
      <sl-dialog class="full-page-on-phone">
        <div slot="label">${label}</div>
        <div>${content}</div>
        ${
          headerActions
            ? html`<div slot="header-actions">${headerActions}</div>`
            : ''
        }
        ${
          footer
            ? html`<div slot="footer" class="dialog-footer">${footer}</div>`
            : ''
        }
      </sl-dialog>
    `;
  }

  footer(): Awaitable {
    return html`<sl-button slot="footer" variant="primary" @click=${this.close}
      >${write`${'ref:dialog:close'}Close`}</sl-button
    >`;
  }
}
@customElement('ep-simple-dialog')
export class SimpleDialog extends DialogBase {
  @property({ type: Object })
  options: {
    label?: Awaitable;
    headerActions?: Awaitable;
    content?: Awaitable;
    footer?: Awaitable;
  } = {};

  public static build(options: {
    label?: Awaitable;
    header?: Awaitable;
    content?: Awaitable;
    footer?: Awaitable;
  }) {
    const dialog = new SimpleDialog();
    dialog.options = options;
    return dialog;
  }

  override content() {
    return this.options.content ?? super.content();
  }
  protected override headerActions(): Awaitable {
    return this.options.headerActions ?? super.headerActions();
  }

  override footer() {
    return this.options.footer ?? super.footer();
  }
}
